/**
 * The shell's glyphs: the ones every game draws (the tabs, the rows of a
 * profile or settings page, the actions of a lobby). A game adds only the
 * glyphs that are its own (a coin, a piece); it never redraws one of these.
 *
 * The owner's drawings, from TwinTen's set (the visual reference), with the
 * few Interlock added (`person`, `menu`, `exit`, `key`, `list`, `code`) and
 * two new ones (`copy`, `share`). One family: a 24-unit box, 2-unit strokes
 * with round caps and joins for line icons (chevrons 2.5, the check 3), and
 * filled shapes for marks (the trophy, the cog, the people).
 *
 * The row-badge glyphs (`speaker` … `info`) are drawn for 18px on a
 * `RowBadge`. Where one has a cut-out (the shield's tick, the question
 * mark, the info stem), the cut-out is painted in `--ui-ground`, the way
 * TwinTen painted it.
 */
export const ICON_STROKES = {
  chat: 'M4 5h16v11H9l-5 4V5Z',
  plus: 'M12 5v14M5 12h14',
  swords: 'M4 4l9 9M4 4h4M4 4v4M20 4l-9 9M20 4h-4M20 4v4M7 14l3 3M17 14l-3 3M5 19l3-3M19 19l-3-3',
  join: 'M10 17l5-5-5-5M15 12H3M14 4h5a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-5',
  book: 'M5 4h10a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4ZM5 17a3 3 0 0 1 3-3h10',
  replay: 'M4 12a8 8 0 1 0 2.4-5.7M4 4v5h5',
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  paste: 'M9 4h6v3H9V4ZM7 5.5H5V21h14V5.5h-2',
  hourglass: 'M7 3h10M7 21h10M8 3c0 5 8 5 8 9s-8 4-8 9M16 3c0 5-8 5-8 9s8 4 8 9',
  close: 'M6 6l12 12M18 6 6 18',
  addUser: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM2 21a7 7 0 0 1 14 0M19 8v6M16 11h6',
  send: 'M4 12 20 4l-6 16-3-7-7-1Z',
  home: 'M4 11.5 12 4l8 7.5M6 10v9h12v-9',
  person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20c.6-3.8 3.7-6 7.5-6s6.9 2.2 7.5 6',
  menu: 'M4 7h16M4 12h16M4 17h16',
  exit: 'M10 4.5H5.5v15H10M14.5 8.5 18 12l-3.5 3.5M18 12H9.5',
  key: 'M8 19a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM10.5 13 19.5 4M15.5 8l2.5 2.5M18 5.5l2 2',
  list: 'M9 6.5h11M9 12h11M9 17.5h11M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01',
  code: 'M5 7h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2ZM7.5 12h.01M12 12h.01M16.5 12h.01',
  copy: 'M10 9h8a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1ZM15 9V5a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h3',
  share: 'M12 15V4M8 8l4-4 4 4M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7',
  // The accent lane: a painter's palette with three dabs (the accent row).
  palette:
    'M12 3.5c-4.8 0-8.5 3.6-8.5 8.3 0 4.5 3.6 8.2 8 8.2 1.2 0 1.8-.7 1.8-1.6 0-.5-.2-.9-.5-1.3-.3-.4-.5-.8-.5-1.3 0-.9.7-1.6 1.6-1.6h2c2.6 0 4.6-2.1 4.6-4.7 0-3.6-3.8-6-8.5-6ZM7.5 12h.01M9.5 8h.01M14.5 8h.01',
} as const;

/** Every glyph the shell draws. An unknown name is a type error. */
export type IconName =
  | keyof typeof ICON_STROKES
  | 'play'
  | 'trophy'
  | 'calendar'
  | 'starFilled'
  | 'starOutline'
  | 'chevronLeft'
  | 'chevronRight'
  | 'gear'
  | 'clock'
  | 'image'
  | 'lock'
  | 'check'
  | 'retry'
  | 'together'
  | 'more'
  | 'speaker'
  | 'vibrate'
  | 'moon'
  | 'globe'
  | 'account'
  | 'shield'
  | 'cap'
  | 'question'
  | 'info';

/** The names, for a test or a game's own picker of glyphs. */
export const ICON_NAMES: readonly IconName[] = [
  ...(Object.keys(ICON_STROKES) as (keyof typeof ICON_STROKES)[]),
  'play',
  'trophy',
  'calendar',
  'starFilled',
  'starOutline',
  'chevronLeft',
  'chevronRight',
  'gear',
  'clock',
  'image',
  'lock',
  'check',
  'retry',
  'together',
  'more',
  'speaker',
  'vibrate',
  'moon',
  'globe',
  'account',
  'shield',
  'cap',
  'question',
  'info',
];

/** The one stroked path of a line icon, or undefined for the drawn ones. */
export function iconStroke(name: IconName): string | undefined {
  return (ICON_STROKES as Record<string, string>)[name];
}
